import { useMemo, useState } from 'react';
import useSWR from 'swr';
import axiosInstance, { API_ENDPOINTS, fetcher } from 'src/utils/axios';

function useGetCollegiateInterestReport(schoolId, includeDetails = true) {
  const [lastValidated, setLastValidated] = useState(null);
  const URL = schoolId
    ? `/api/career/collegiate-interests?schoolId=${schoolId}&includeDetails=${includeDetails}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    (url) => axiosInstance.get(url).then((res) => res.data),
    {
      onSuccess: () => setLastValidated(new Date()),
    }
  );

  const memoizedValue = useMemo(
    () => ({
      collegiateInterestReport: data,
      collegiateInterestReportLoading: isLoading,
      collegiateInterestReportError: error,
      collegiateInterestReportValidating: isValidating,
      collegiateInterestReportEmpty: !isLoading && (!data || Object.keys(data).length === 0),
      lastValidated,
      revalidateCollegiateInterestReport: mutate,
    }),
    [data, error, isLoading, isValidating, lastValidated, mutate]
  );

  return memoizedValue;
}

function useGetStudentInterestReport(schoolId, includeDetails = true) {
  const [lastValidated, setLastValidated] = useState(null);
  const URL = schoolId
    ? `/api/career/student-interests?schoolId=${schoolId}&includeDetails=${includeDetails}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    (url) => axiosInstance.get(url).then((res) => res.data),
    {
      onSuccess: () => setLastValidated(new Date()),
    }
  );

  const memoizedValue = useMemo(
    () => ({
      studentInterestReport: data,
      studentInterestReportLoading: isLoading,
      studentInterestReportError: error,
      studentInterestReportValidating: isValidating,
      studentInterestReportEmpty: !isLoading && (!data || Object.keys(data).length === 0),
      lastValidated,
      revalidateStudentInterestReport: mutate,
    }),
    [data, error, isLoading, isValidating, lastValidated, mutate]
  );

  return memoizedValue;
}

function useGetLastVisitReport(schoolId) {
  const [lastValidated, setLastValidated] = useState(null);
  const URL = schoolId ? `/api/career/last-visit-report?schoolId=${schoolId}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    (url) => axiosInstance.get(url).then((res) => res.data),
    {
      onSuccess: () => setLastValidated(new Date()),
    }
  );

  const memoizedValue = useMemo(
    () => ({
      lastVisitReport: data,
      lastVisitReportLoading: isLoading,
      lastVisitReportError: error,
      lastVisitReportValidating: isValidating,
      lastVisitReportEmpty: !isLoading && (!data || Object.keys(data).length === 0),
      lastValidated,
      revalidateLastVisitReport: mutate,
    }),
    [data, error, isLoading, isValidating, lastValidated, mutate]
  );

  return memoizedValue;
}

// The Careers caseload (#612): every Grade 9+ student at the school with the
// derived fields the dashboard filters and sorts on, in one call
function useGetCaseload(schoolId) {
  const { data, isLoading, error, mutate } = useSWR(
    schoolId ? `${API_ENDPOINTS.career.caseload}?schoolId=${schoolId}` : null,
    fetcher
  );

  return useMemo(
    () => ({
      caseload: data?.students,
      caseloadToday: data?.today,
      // Whether the Careers AI features are on: the flags and suggested industry
      caseloadAi: Boolean(data?.ai),
      caseloadLoading: isLoading,
      caseloadError: error,
      revalidateCaseload: mutate,
    }),
    [data, error, isLoading, mutate]
  );
}

/** Set one next visit for several students at once: { asns, by } or { asns, noFollowUp: true } (#613). */
async function setNextVisits(schoolId, payload) {
  const { data } = await axiosInstance.put(`${API_ENDPOINTS.career.nextVisits}?schoolId=${schoolId}`, payload);
  return data; // { updated, skipped: [{ asn, code }] }
}

export { useGetCollegiateInterestReport, useGetStudentInterestReport, useGetLastVisitReport, useGetCaseload, setNextVisits };
